import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company } from '../@types/Company';
import type { CoproReferentMappingData } from '../@types/MailConfig';
import type { Owner } from '../@types/Owner';
import type { RootState } from '../store/store';
import { JWTAuthorization, JWTHeader } from '../utils/JWTHeader';
import type { ServedCompany } from '../utils/ownerCompany';
import { findServedCompany, resolveOwnerCompany, resolveOwnerCoproId } from '../utils/ownerCompany';
import { DJ_URL } from '../utils/routes';

interface ReferentSendAvailability {
  coproMappings: CoproReferentMappingData[];
  verifiedReferentIds: string[];
  servedCompanies: ServedCompany[];
}

const EMPTY_AVAILABILITY: ReferentSendAvailability = {
  coproMappings: [],
  verifiedReferentIds: [],
  servedCompanies: [],
};

/**
 * Determines whether the "Envoyer depuis le mail référent" action can be offered
 * for an owner. Relies on the lightweight, non-privileged availability endpoint
 * (CAN_GET_COMPANY) so that any team member — not only the elevated setup
 * functions that manage the mail configuration — gets the correct button state.
 */
export function useReferentSendAvailability(companyId: number | null | undefined): {
  canOwnerSendFromReferent: (owner: Owner) => boolean;
  getOwnerCompany: (owner: Owner) => Company | null;
  getOwnerPrivacyPolicyLink: (owner: Owner, currentCompanyLink: string | null) => string | null;
  getServedReferentEmail: (owner: Owner) => string | null;
} {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const [availability, setAvailability] = useState<ReferentSendAvailability>(EMPTY_AVAILABILITY);
  const [servedPrivacyPolicyLinks, setServedPrivacyPolicyLinks] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!companyId || !jwt) {
      setAvailability(EMPTY_AVAILABILITY);
      return;
    }

    let aborted = false;
    const fetchAvailability = async() => {
      try {
        const res = await fetch(`/api/companies/${companyId}/mail-config/referent-availability`, {
          headers: { Authorization: JWTAuthorization(jwt) },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        if (aborted) { return; }
        setAvailability({
          coproMappings: json.coproMappings ?? [],
          verifiedReferentIds: json.verifiedReferentIds ?? [],
          servedCompanies: json.servedCompanies ?? [],
        });
      } catch (error) {
        if (aborted) { return; }
        console.error('[useReferentSendAvailability] Error fetching referent availability:', error);
        setAvailability(EMPTY_AVAILABILITY);
      }
    };

    fetchAvailability();
    return () => { aborted = true; };
  }, [companyId, jwt]);

  useEffect(() => {
    if (!jwt || availability.servedCompanies.length === 0) {
      return;
    }

    let aborted = false;
    availability.servedCompanies.forEach(async({ siren }) => {
      try {
        const res = await fetch(`/api/public/dj/companies/${siren}/privacy-policy`, JWTHeader(jwt));
        const json = res.ok ? await res.json() : null;
        if (!aborted && json?.link) {
          setServedPrivacyPolicyLinks((links) => ({ ...links, [siren]: `${DJ_URL}${json.link}` }));
        }
      } catch (error) {
        console.error('[useReferentSendAvailability] Error fetching privacy policy link:', error);
      }
    });
    return () => { aborted = true; };
  }, [availability.servedCompanies, jwt]);

  const canOwnerSendFromReferent = useCallback((owner: Owner): boolean => {
    const coproId = resolveOwnerCoproId(owner);
    if (!coproId) {
      return false;
    }
    const mapping = availability.coproMappings.find((m) => m.coproId === coproId);
    if (!mapping) {
      return false;
    }
    return availability.verifiedReferentIds.includes(mapping.brandingReferentId);
  }, [availability]);

  const getOwnerCompany = useCallback(
    (owner: Owner): Company | null => resolveOwnerCompany(owner, company, availability.servedCompanies),
    [company, availability.servedCompanies],
  );

  const getOwnerPrivacyPolicyLink = useCallback((owner: Owner, currentCompanyLink: string | null): string | null => {
    const served = findServedCompany(owner, availability.servedCompanies);
    return served ? servedPrivacyPolicyLinks[served.siren] ?? null : currentCompanyLink;
  }, [availability.servedCompanies, servedPrivacyPolicyLinks]);

  const getServedReferentEmail = useCallback((owner: Owner): string | null => {
    const served = findServedCompany(owner, availability.servedCompanies);
    const mapping = availability.coproMappings.find((m) => m.coproId === resolveOwnerCoproId(owner));
    const referent = served?.brandingSettings.referents.find((r) => r.id === mapping?.brandingReferentId);
    return referent?.email ?? null;
  }, [availability]);

  return { canOwnerSendFromReferent, getOwnerCompany, getOwnerPrivacyPolicyLink, getServedReferentEmail };
}

export default useReferentSendAvailability;
